// Name formatting for the v2 screen — preserves the v1 TV behaviour, but the
// masking logic lives here (v2-owned) rather than importing the v1 util.
//
// display_prn_tv         -> show the booking's PRN instead of the name.
// mask_patient_name_tv   -> mask the patient name (PR**** style).
// announce_first_name_tv -> reduce to first name only.
//
// display_prn_tv wins over the other two, but only when the booking actually
// carries a PRN — it is frequently empty, so the no-PRN path (falling through
// to the masking rules below) is the common case, not an edge case.
import { useCallback } from "react";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";

// First name's first 2 chars + up to 4 stars, e.g. "Praveen" -> "PR****".
export const maskPatientName = (name?: string | null): string => {
  if (!name || !name.trim()) return "";
  const firstName = name.trim().toUpperCase().split(/\s+/)[0];
  const prefix = firstName.slice(0, 2);
  const maskLength = Math.min(Math.max(firstName.length - 2, 0), 4);
  return prefix + "*".repeat(maskLength);
};

export const useDisplayName = () => {
  const {
    mask_patient_name_tv,
    announce_first_name_tv,
    display_prn_tv,
  } = useFeatureFlags();

  return useCallback(
    (raw?: string | null, prn?: string | null): string => {
      // A PRN is not a name, so it is shown as-is — never masked or reduced.
      if (display_prn_tv) {
        const prnValue = prn?.trim();
        if (prnValue) return prnValue;
      }
      if (mask_patient_name_tv) return maskPatientName(raw);
      const name = raw ?? "";
      if (announce_first_name_tv) {
        return name.trim().split(/\s+/)[0] ?? "";
      }
      return name;
    },
    [mask_patient_name_tv, announce_first_name_tv, display_prn_tv]
  );
};
